/**
 * Label, control, and hint-or-error, drawn as finixt-ui's Field with the
 * wiring the package's own leaves out: the label points at the control, the
 * hint and the error carry ids a control can be described by, and the error
 * is announced. The error replaces the hint; FINIXT never stacks both.
 */
import type { CSSProperties, ReactNode } from 'react';

import { Icon } from './finixt.js';

export interface FieldProps {
  /** The control's id. Omit for a control that is labelled another way. */
  id?: string | undefined;
  label: string;
  /** Adds a danger asterisk to the label. */
  required?: boolean | undefined;
  hint?: string | undefined;
  error?: string | undefined;
  children: ReactNode;
  style?: CSSProperties | undefined;
}

export function Field({ id, label, required, hint, error, children, style }: FieldProps) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5, minWidth: 0, ...style }}>
      <label
        htmlFor={id}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 5,
          fontSize: 'var(--text-meta-lg)',
          fontWeight: 'var(--weight-medium)',
          color: 'var(--text-muted)',
        }}
      >
        {label}
        {required ? (
          <span aria-hidden="true" style={{ color: 'var(--danger)' }}>
            *
          </span>
        ) : null}
      </label>
      {children}
      {error ? (
        <span
          id={id ? `${id}-error` : undefined}
          role="alert"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 5,
            fontSize: 'var(--text-meta)',
            color: 'var(--danger)',
          }}
        >
          <Icon name="error" size={14} />
          {error}
        </span>
      ) : hint ? (
        <span
          id={id ? `${id}-hint` : undefined}
          style={{ fontSize: 'var(--text-meta)', color: 'var(--text-faint)' }}
        >
          {hint}
        </span>
      ) : null}
    </div>
  );
}
